import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tenantsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import {
  ArrowLeft, Building2, Users, Package, Shield, Plus, UserPlus, Pause, Play,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import RequiredDocumentTypesManager from '../../components/RequiredDocumentTypesManager';

const AVAILABLE_MODULES = [
  { key: 'timesheet_ocr', label: 'Timesheets' },
  { key: 'accounting_intake', label: 'Accounting Intake' },
  { key: 'contracts', label: 'Contracts' },
  { key: 'accounting_repository', label: 'Accounting Repository' },
  { key: 'document_submission', label: 'Document Submission' },
];

const PlatformTenantDetailPage = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [savingModules, setSavingModules] = useState(false);
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [adminForm, setAdminForm] = useState({ email: '', password: '', first_name: '', last_name: '' });

  useEffect(() => { loadData(); }, [tenantId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tenantRes, usersRes] = await Promise.all([
        tenantsAPI.get(tenantId),
        tenantsAPI.listUsers(tenantId, {}),
      ]);
      setTenant(tenantRes.data);
      setModules(tenantRes.data.enabled_modules || []);
      setUsers(usersRes.data);
    } catch (e) {
      toast.error('Failed to load tenant');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleModule = (key) => {
    setModules((prev) => prev.includes(key) ? prev.filter((m) => m !== key) : [...prev, key]);
  };

  const handleSaveModules = async () => {
    setSavingModules(true);
    try {
      await tenantsAPI.assignModules(tenantId, modules);
      toast.success('Modules updated');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update modules');
    } finally {
      setSavingModules(false);
    }
  };

  const handleCreateAdmin = async () => {
    if (!adminForm.email || !adminForm.password || !adminForm.first_name) {
      toast.error('Fill in all required fields');
      return;
    }
    try {
      await tenantsAPI.createTenantAdmin(tenantId, adminForm);
      toast.success('Tenant admin created');
      setAdminDialogOpen(false);
      setAdminForm({ email: '', password: '', first_name: '', last_name: '' });
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create admin');
    }
  };

  const handleSuspend = async () => {
    try {
      await tenantsAPI.suspend(tenantId);
      toast.success('Tenant suspended');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to suspend');
    }
  };

  const handleReactivate = async () => {
    try {
      await tenantsAPI.reactivate(tenantId);
      toast.success('Tenant reactivated');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reactivate');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-60" />)}</div>
      </div>
    );
  }

  if (!tenant) {
    return <div className="text-center py-16 text-slate-500">Tenant not found</div>;
  }

  const hasAdmin = users.some((u) => u.role === 'tenant_admin' || u.role === 'super_admin');

  return (
    <div className="space-y-6" data-testid="tenant-detail-page">
      {/* Back + Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/platform/tenants')} data-testid="back-to-tenants">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-slate-900">{tenant.name}</h1>
            <Badge className={tenant.is_active ? 'bg-emerald-100 text-emerald-700 border-0' : 'bg-red-100 text-red-700 border-0'}>
              {tenant.is_active ? 'Active' : 'Suspended'}
            </Badge>
          </div>
          <p className="text-sm text-slate-500 font-mono mt-0.5">{tenant.slug}</p>
        </div>
        {tenant.is_active ? (
          <Button variant="outline" className="text-red-600 border-red-200" onClick={handleSuspend} data-testid="suspend-btn">
            <Pause className="mr-2 h-4 w-4" /> Suspend Tenant
          </Button>
        ) : (
          <Button variant="outline" className="text-emerald-600 border-emerald-200" onClick={handleReactivate} data-testid="reactivate-btn">
            <Play className="mr-2 h-4 w-4" /> Reactivate Tenant
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Module Assignment */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-indigo-600" />
              <CardTitle>Module Assignment</CardTitle>
            </div>
            <CardDescription>Enable or disable modules for this tenant</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {AVAILABLE_MODULES.map((m) => (
                <div key={m.key} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{m.label}</p>
                    <p className="text-xs text-slate-500 font-mono">{m.key}</p>
                  </div>
                  <Switch
                    checked={modules.includes(m.key)}
                    onCheckedChange={() => handleToggleModule(m.key)}
                    data-testid={`module-toggle-${m.key}`}
                  />
                </div>
              ))}
              <Button onClick={handleSaveModules} disabled={savingModules} className="w-full mt-2" data-testid="save-modules-btn">
                {savingModules ? 'Saving...' : 'Save Module Changes'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tenant Info */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-indigo-600" />
              <CardTitle>Tenant Info</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">ID</p>
                <p className="text-sm font-mono text-slate-700 truncate">{tenant.id}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Created</p>
                <p className="text-sm text-slate-700">{format(new Date(tenant.created_at), 'MMM d, yyyy')}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg col-span-2">
                <p className="text-xs text-slate-500">Description</p>
                <p className="text-sm text-slate-700">{tenant.description || '-'}</p>
              </div>
            </div>

            {!hasAdmin && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 font-medium">No Tenant Admin assigned</p>
                <p className="text-xs text-amber-600 mt-1">Create an admin so the tenant can manage their workspace.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Required Document Types (only shown if document_submission module is enabled) */}
      {modules.includes('document_submission') && (
        <RequiredDocumentTypesManager tenantId={tenantId} enabled />
      )}

      {/* Users */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-indigo-600" />
              <CardTitle>Tenant Users ({users.length})</CardTitle>
            </div>
            <Dialog open={adminDialogOpen} onOpenChange={setAdminDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="create-tenant-admin-btn">
                  <UserPlus className="mr-2 h-4 w-4" /> Create Tenant Admin
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Tenant Admin</DialogTitle>
                  <DialogDescription>Create an admin user for {tenant.name}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>First Name *</Label>
                      <Input value={adminForm.first_name} onChange={(e) => setAdminForm((f) => ({ ...f, first_name: e.target.value }))} data-testid="admin-firstname" />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Name</Label>
                      <Input value={adminForm.last_name} onChange={(e) => setAdminForm((f) => ({ ...f, last_name: e.target.value }))} data-testid="admin-lastname" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input type="email" value={adminForm.email} onChange={(e) => setAdminForm((f) => ({ ...f, email: e.target.value }))} data-testid="admin-email" />
                  </div>
                  <div className="space-y-2">
                    <Label>Password *</Label>
                    <Input type="password" value={adminForm.password} onChange={(e) => setAdminForm((f) => ({ ...f, password: e.target.value }))} data-testid="admin-password" />
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <Button variant="outline" onClick={() => setAdminDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateAdmin} data-testid="save-tenant-admin-btn">Create Admin</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No users yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold uppercase">User</TableHead>
                  <TableHead className="text-xs font-semibold uppercase">Email</TableHead>
                  <TableHead className="text-xs font-semibold uppercase">Role</TableHead>
                  <TableHead className="text-xs font-semibold uppercase">Status</TableHead>
                  <TableHead className="text-xs font-semibold uppercase">Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`tenant-user-${u.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-indigo-100 text-indigo-600 text-xs">
                            {(u.first_name?.[0] || '') + (u.last_name?.[0] || '')}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-sm">{u.first_name} {u.last_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs capitalize">
                        <Shield className="mr-1 h-3 w-3" />
                        {u.role?.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={u.is_active ? 'bg-emerald-100 text-emerald-700 border-0' : 'bg-red-100 text-red-700 border-0'}>
                        {u.is_active ? 'Active' : 'Disabled'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">{u.created_at && format(new Date(u.created_at), 'MMM d, yyyy')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PlatformTenantDetailPage;
